import type { LucideIcon } from 'lucide-react';
import { MoreHorizontal } from 'lucide-react';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '@/lib/utils';

/**
 * One action on one or more products. The products page builds a single list of these and hands
 * it to the card menu, the list-row menu and the selection bar, so all three always offer the same
 * actions in the same order.
 */
export type ProductAction = {
	id: string;
	label: string;
	icon: LucideIcon;
	onClick: () => void;
	destructive?: boolean;
};

/** The "…" menu on a product card or row. Destructive actions sit below a separator. */
export function ProductActionMenu({
	actions,
	className,
}: {
	actions: ProductAction[];
	className?: string;
}) {
	if (actions.length === 0) return null;
	const regular = actions.filter((a) => !a.destructive);
	const destructive = actions.filter((a) => a.destructive);

	const item = (action: ProductAction) => (
		<DropdownMenuItem
			key={action.id}
			variant={action.destructive ? 'destructive' : undefined}
			onClick={(e) => {
				e.stopPropagation();
				action.onClick();
			}}
		>
			<action.icon size={14} /> {action.label}
		</DropdownMenuItem>
	);

	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				onClick={(e) => e.stopPropagation()}
				aria-label="Product actions"
				className={cn(
					'flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1',
					className
				)}
			>
				<MoreHorizontal size={14} />
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end">
				{regular.map(item)}
				{regular.length > 0 && destructive.length > 0 && <DropdownMenuSeparator />}
				{destructive.map(item)}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
